import * as React from 'react';
import { clamp, sumBy } from 'es-toolkit';
import { cn } from '../utils';
import { getTestId, type TestIdProps } from '../utils/testId';
import {
  progressFillVariants,
  progressTrackVariants,
  type ProgressTrackVariantProps
} from './progress-bar-variants';
import { stackProgressSegments, type ProgressBarSegment } from './stack-progress-segments';

export type { ProgressBarSegment } from './stack-progress-segments';

export interface StackedProgressBarProps extends ProgressTrackVariantProps, TestIdProps {
  /** Parts drawn side by side in order; their total is clamped to 100. */
  segments: readonly ProgressBarSegment[];
  /** Accessible label describing what the bar represents. */
  ariaLabel?: string;
  /** The value to report instead of the rounded total, e.g. the percentage the text beside says. */
  ariaValueNow?: number;
  /** How the value reads aloud, e.g. "45 % used, over the limit". */
  ariaValueText?: string;
  className?: string;
}

/** A progress bar of several parts side by side, each in its own colour. */
export const StackedProgressBar = React.forwardRef<HTMLDivElement, StackedProgressBarProps>(
  (
    { segments, size, muted, ariaLabel, ariaValueNow, ariaValueText, className, testId, id, name },
    ref
  ) => {
    const stacked = stackProgressSegments(segments);
    const total = sumBy(stacked, segment => segment.value);
    const valueNow = clamp(Math.round(ariaValueNow ?? total), 0, 100);

    return (
      <div
        ref={ref}
        role='progressbar'
        aria-valuenow={valueNow}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={ariaValueText}
        aria-label={ariaLabel}
        data-testid={getTestId({ testId, id, name })}
        className={cn(progressTrackVariants({ size, muted }), 'flex', className)}
      >
        {stacked.map((segment, index) => (
          <div
            // The list is the caller's fixed order, so the position is the segment's identity.
            key={index}
            data-slot='progress-segment'
            title={segment.label}
            className={progressFillVariants({ variant: segment.variant, segment: true })}
            style={{ width: `${segment.value}%` }}
          />
        ))}
      </div>
    );
  }
);

StackedProgressBar.displayName = 'StackedProgressBar';
